import {
  Users, HardDrive, UserCog, DatabaseBackup, Clock, Boxes,
  ShieldCheck, Lock, BrickWall, Monitor, Settings, DoorOpen,
  EyeOff, KeyRound, Fingerprint, UsersRound, Crown, ScrollText,
  FolderTree, Key, Search
} from 'lucide-react';
import { AVAILABLE_ICONS } from '../../admincomponents/CategoryIconPicker';

/**
 * Default image mapping for finding icons.
 * Matches the backend implementation.
 */
export const DEFAULT_IMAGE_MAP = {
  'Accounts': 'user.png',
  'Drive': 'drive.png',
  'Admins': 'admin.png',
  'Backups': 'backup.png',
  'Age': 'age.png',
  'Softwares': 'softwares.png',
  'Antivirus': 'antivirus.png',
  'Encryption': 'encryption.png',
  'Firewall': 'firewall.png',
  'OS': 'os.png',
  'Services': 'services.png',
  'Open Ports': 'port.png',
  'Dark Web': 'dark_web.png',
  'AD Computers': 'computer.png',
  'AD Users': 'user.png',
  'MS Users': 'user.png',
  'MFA': 'MFA.png',
  'PII': 'pii.png',
  'AD Groups': 'group.png',
  'FSMO': 'fsmo.png',
  'GPO': 'gpo.png',
  'OU': 'ou.png',
  'Passwords': 'passwords.png',
  'Policy': 'gpo.png'
};

/**
 * Dynamic SVG Icon Mapping using lucide-react.
 * Allows icons to inherit dynamic template status colors.
 */
export const FINDING_ICON_MAP = {
  'Accounts': Users,
  'Drive': HardDrive,
  'Admins': UserCog,
  'Backups': DatabaseBackup,
  'Age': Clock,
  'Softwares': Boxes,
  'Antivirus': ShieldCheck,
  'Encryption': Lock,
  'Firewall': BrickWall,
  'OS': Monitor,
  'Services': Settings,
  'Open Ports': DoorOpen,
  'Dark Web': EyeOff,
  'AD Computers': Monitor,
  'AD Users': Users,
  'MS Users': Users,
  'MFA': KeyRound,
  'PII': Fingerprint,
  'AD Groups': UsersRound,
  'FSMO': Crown,
  'GPO': ScrollText,
  'OU': FolderTree,
  'Passwords': Key,
  'Policy': ScrollText
};

/**
 * Resolves the dynamic SVG icon component for a finding.
 * 
 * @param {string} name - Finding name
 * @param {string|null} iconOverrideName - Optional explicit name of a lucide-react icon to use instead.
 * @returns {React.Component} The Lucide-React icon component, or a fallback icon.
 */
export const getFindingSvgIconComponent = (name, iconOverrideName = null) => {
  if (iconOverrideName && AVAILABLE_ICONS[iconOverrideName]) {
    return AVAILABLE_ICONS[iconOverrideName];
  }
  return FINDING_ICON_MAP[name] || Search; // Search as fallback
};

/**
 * Resolves the icon path for a finding, taking into account status suffixes
 * and custom icon overrides.
 *
 * @param {string} name - Finding name
 * @param {number|null} score - Numerical score (0-100)
 * @param {Object} overrides - Style overrides (block.style.findingOverrides[name])
 * @param {string} imageBaseUrl - Base URL for default icons
 * @returns {string} Fully resolved icon URL
 */
export const getFindingIcon = (name, score, overrides = {}, imageBaseUrl = '/s3/images') => {
  // 1. Check for custom icon override first
  if (overrides && overrides.customIcon) {
    return overrides.customIcon;
  }

  // 2. Resolve default icon with status suffix
  const baseImage = DEFAULT_IMAGE_MAP[name] || 'user.png';
  let suffix = '_b'; // Default to black/gray

  if (score !== null && score !== undefined) {
    if (score < 20) {
      suffix = '_g'; // Green
    } else if (score < 50) {
      suffix = '_y'; // Yellow
    } else {
      suffix = '_r'; // Red
    }
  }

  const imageNameWithoutExt = baseImage.replace('.png', '').replace('.PNG', '');
  const extension = baseImage.toLowerCase().endsWith('.png') ? '.png' : '.png'; // Standardizing on .png for status icons

  return `${imageBaseUrl}/${imageNameWithoutExt}${suffix}${extension}`;
};
